import { MODEL_NAMES } from '../../constants/index';
import { contactIdentityKey } from '../../utils/messaging/contact-identity';
import { didCancel, task } from 'ember-concurrency';
import { jsonApiErrorTitle } from '../../utils/messaging/conversation';
import { matchingUsers } from '../../utils/messaging/user';
import { service } from '@ember/service';
import MessagingRoutesContactsHandler from './contacts-handler.js';

export default class MessagingRoutesModifyMembersContactsHandler extends MessagingRoutesContactsHandler {
  @service store;

  titleKey = 'emc.modify_members.title';
  ctaKey = 'emc.modify_members.confirm';
  showListSections = true;
  multiSelect = true;
  showScopeIndicator = true;
  showEmptyStateBanner = true;
  showNoSearchResultsBanner = true;

  get relatedEmptyStateKey() {
    return 'emc.modify_members.no_related_contacts_found';
  }

  get conversation() {
    return this.options.conversation ?? this.options.parentHandler?.conversation;
  }

  get isSubmitDisabled() {
    if (this.persistRoster.isRunning) return true;
    return this.submitCount < 1 || !this.hasRosterChanged;
  }

  get hasRosterChanged() {
    const currentKeys = this.selectedContactIds;
    const baselineKeys = this.#rosterContacts(this.messaging.currentProfile?.user).map(
      contactIdentityKey
    );

    if (currentKeys.size !== baselineKeys.length) return true;
    return !baselineKeys.every(key => currentKeys.has(key));
  }

  augmentFilter(filter, { relatedClientsOnly } = {}) {
    if (relatedClientsOnly) {
      return { ...filter, related_clients_only: true };
    }
    return filter;
  }

  prepareSelection(currentUser) {
    for (const contact of this.#rosterContacts(currentUser)) {
      this.rememberSelectedContact(contact);
    }
  }

  submit(selectedContacts) {
    return this.persistRoster.perform(selectedContacts);
  }

  leave() {
    // Cancel: pop back to the screen that opened this one.
    return this.messagingNavigation.leaveChildRoute();
  }

  persistRoster = task({ drop: true }, async selectedContacts => {
    const conversation = this.conversation;
    const sourceId = String(conversation.id);

    try {
      const payload = await this.store
        .adapterFor(MODEL_NAMES.CONVERSATION)
        .updateDraftMembers(conversation, selectedContacts);

      this.store.pushPayload(MODEL_NAMES.CONVERSATION, payload);
      const updatedId = String(payload.data.id);
      const updated = this.#peekConversation(updatedId);
      if (!updated) {
        throw new Error(`Conversation ${updatedId} missing after updateDraftMembers`);
      }

      if (updatedId !== sourceId) {
        // That roster already exists. Open its thread so the existing
        // message history is visible immediately.
        this.#transferMessageDraft(conversation.messageDraft, updated);
        this.messaging.replaceConversationInList(sourceId, updated);
        this.messagingNavigation.invalidateHistory(sourceId);
        await this.messagingNavigation.toConversation(updated, { recordHistory: false });
        this.#unloadConversation(sourceId);
        return;
      }

      // Same conversation: pop back to the opener (thread or details).
      await this.messagingNavigation.leaveChildRoute();
      this.toast.success(this.intl.t('emc.modify_members.success'));
    } catch (error) {
      if (didCancel(error)) return;
      this.messagingLogging.error(error);
      this.toast.error(jsonApiErrorTitle(error) ?? this.intl.t('emc.general_error'));
    }
  });

  #peekConversation(id) {
    return this.store.peekRecord(MODEL_NAMES.CONVERSATION, String(id));
  }

  #unloadConversation(id) {
    const record = this.#peekConversation(id);
    if (record && !record.isDestroying && !record.isDestroyed) {
      this.store.unloadRecord(record);
    }
  }

  #transferMessageDraft(capturedDraft, newRecord) {
    if (capturedDraft) {
      newRecord.messageDraft ||= capturedDraft;
    }
  }

  #rosterContacts(currentUser) {
    const members = this.conversation?.collapsedMembers ?? [];
    return members
      .filter(member => member.isCouple || !(currentUser && matchingUsers(member, currentUser)))
      .map(member => this.#rosterContact(member));
  }

  #rosterContact(member) {
    const contact = {
      id: String(member.externalId),
      externalId: String(member.externalId),
      externalScope: member.externalScope,
      name: member.name,
      isClient: member.isClient,
      organizationId: member.organizationId ?? member.members?.[0]?.organizationId,
    };
    if (member.isCouple) {
      contact.couple = (member.members ?? []).map(partner => this.#rosterContact(partner));
    }
    return contact;
  }
}
